import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { useDashboard } from '../../../../../contexts';
import { useAccountStatisticsData } from '../../../../../hooks';
import { formatCurrency, formatDuration } from '../../utils';
import { StatisticCard } from '../statistic-card';
import { StatisticCardProps } from '../statistics-grid';

export const ForexStatisticsGrid: FC = () => {
  const { account } = useDashboard();

  const { t } = useTranslation('accountStatistics');

  const { data: accountStats } = useAccountStatisticsData(
    account?.tsAccountId || '',
    account || undefined,
  );

  if (!account || !accountStats) {
    return null;
  }

  const forexStatisticsData: StatisticCardProps[] = [
    // Row 1
    {
      label: t('statistics.netProfit.label'),
      tooltip: t('statistics.netProfit.tooltip'),
      value: accountStats.totalNetProfit
        ? formatCurrency(accountStats.totalNetProfit)
        : '$0.00',
    },
    {
      label: t('statistics.grossProfit.label'),
      tooltip: t('statistics.grossProfit.tooltip'),
      value: accountStats.grossProfit
        ? formatCurrency(accountStats.grossProfit)
        : '$0.00',
    },
    {
      label: t('statistics.grossLoss.label'),
      tooltip: t('statistics.grossLoss.tooltip'),
      value: accountStats.grossLoss
        ? formatCurrency(Math.abs(accountStats.grossLoss))
        : '$0.00',
    },
    // Row 2
    {
      label: t('statistics.profitFactor.label'),
      tooltip: t('statistics.profitFactor.tooltip'),
      value: accountStats.profitFactor?.toFixed(2) || '0.00',
    },
    {
      label: t('statistics.bestProfit.label'),
      tooltip: t('statistics.bestProfit.tooltip'),
      value: accountStats.bestProfit
        ? formatCurrency(accountStats.bestProfit)
        : '$0.00',
    },
    {
      label: t('statistics.biggestLoss.label'),
      tooltip: t('statistics.biggestLoss.tooltip'),
      value: accountStats.biggestLoss
        ? formatCurrency(Math.abs(accountStats.biggestLoss))
        : '$0.00',
    },
    // Row 3
    {
      label: t('statistics.expectancy.label'),
      tooltip: t('statistics.expectancy.tooltip'),
      value: accountStats.expectancy
        ? formatCurrency(accountStats.expectancy)
        : '$0.00',
    },
    {
      label: t('statistics.avgTradeSize.label'),
      tooltip: t('statistics.avgTradeSize.tooltip'),
      value: accountStats.avgTradeSize
        ? accountStats.avgTradeSize?.toFixed(2)
        : '0.00',
    },
    {
      label: t('statistics.avgTradeDuration.label'),
      tooltip: t('statistics.avgTradeDuration.tooltip'),
      value: accountStats.avgTradeDuration
        ? formatDuration(accountStats.avgTradeDuration)
        : '0h:0m:0s',
    },
  ];

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Grid container spacing={6}>
        {forexStatisticsData.map((stat: StatisticCardProps, index: number) => (
          <Grid
            size={{
              xs: 6,
              md: 4,
            }}
            key={index}
          >
            <StatisticCard
              label={stat.label}
              tooltip={stat.tooltip}
              value={stat.value}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
